import type { ZudokuConfig } from "zudoku";

import { LangSwitcher } from "./src/LangSwitcher";
import { API_STAGING, OPENAPI_URL } from "./src/urls.generated.mjs";
import "./src/styles/theme.css";

/**
 * Source de la spécification OpenAPI.
 *
 * Le backend exposera `GET <API>/v1/public/openapi.json` : spec filtrée sur
 * `/v1/papi/*`, publique, avec ETag. Tant que cet endpoint n'existe pas, on
 * retombe sur le stub local rendu par `scripts/prepare.mjs`.
 *
 * Bascule : renseigner PUBLIC_OPENAPI_URL dans `.env` ou l'environnement de
 * build. Aucune autre modification n'est nécessaire.
 */
const apiOptions = {
  examplesLanguage: "shell",
  supportedLanguages: [
    { value: "shell", label: "curl" },
    { value: "javascript", label: "JavaScript" },
    { value: "python", label: "Python" },
    { value: "php", label: "PHP" },
  ],
  schemaDownload: { enabled: true, fileName: "instafuel-papi" },
} as const;

const apiSource: ZudokuConfig["apis"] = OPENAPI_URL
  ? { type: "url", input: OPENAPI_URL, path: "/api", options: apiOptions }
  : {
      type: "file",
      input: "./spec/openapi.papi.json",
      path: "/api",
      options: apiOptions,
    };

const config: ZudokuConfig = {
  site: {
    title: "Documentation Instafuel",
    logo: {
      src: { light: "/logo-mark.png", dark: "/logo-mark.png" },
      alt: "Instafuel",
      width: "36px",
      href: "/",
    },
    footer: {
      copyright: "© Instafuel — Abidjan, Côte d'Ivoire",
      columns: [
        {
          title: "Guides",
          links: [
            { label: "Démarrage rapide", href: "/guides/demarrage-rapide" },
            { label: "Clés d'API", href: "/guides/cles-api" },
            { label: "Référence API", href: "/api" },
          ],
        },
        {
          title: "Référence",
          links: [
            { label: "Codes d'erreur", href: "/reference/codes-erreur" },
            { label: "Glossaire", href: "/reference/glossaire" },
            { label: "English", href: "/en/accueil" },
          ],
        },
        {
          // L'adresse de production n'est pas publiée : elle est communiquée
          // avec la clé ifp_live_*, à l'ouverture du compte.
          title: "Environnement",
          links: [
            {
              label: `Staging — ${API_STAGING.replace(/^https:\/\//, "")}`,
              href: API_STAGING,
            },
          ],
        },
      ],
    },
  },

  // Zudoku n'a pas d'i18n : le sélecteur de langue, les hreflang et l'attribut
  // `lang` de la page sont portés par ce composant. Voir src/LangSwitcher.tsx.
  // Un seul point de montage : la barre supérieure est déjà rendue sur mobile,
  // le monter aussi dans `mobile-top-bar-end` afficherait deux sélecteurs.
  slots: {
    "head-navigation-start": <LangSwitcher />,
  },

  metadata: {
    title: "%s — Instafuel",
    defaultTitle: "Documentation Instafuel",
    description:
      "API partenaire Instafuel : gérez votre portefeuille d'entreprises clientes, leurs wallets Fuelz et leurs transactions carburant en Côte d'Ivoire.",
    favicon: "/favicon.svg",
    logo: "/logo-mark.png",
  },

  // Charte Instafuel — ADR-0005. Bleu du logo, neutres chauds.
  // Le fond d'action est brand-700 #0E6AA4 (5,8:1 avec du texte blanc) :
  // brand-500 #26A2EA tombe à 2,8:1 et ne peut pas porter de texte blanc.
  theme: {
    light: {
      background: "#FAFAF7", // n-25
      foreground: "#1A1813", // n-800
      card: "#FFFFFF",
      cardForeground: "#1A1813",
      popover: "#FFFFFF",
      popoverForeground: "#1A1813",
      primary: "#0E6AA4", // brand-700 — fond de CTA, texte blanc AA
      primaryForeground: "#FFFFFF",
      secondary: "#EAE8E0", // n-100
      secondaryForeground: "#2F2B22", // n-700
      muted: "#F4F3EE", // n-50
      mutedForeground: "#595446", // n-600
      accent: "#EAE8E0", // n-100 — survol de surface, neutre chaud
      accentForeground: "#0E6AA4",
      destructive: "#D6362E",
      destructiveForeground: "#FFFFFF",
      border: "#EAE8E0", // n-100
      input: "#D8D5C9", // n-200
      ring: "#26A2EA", // brand-500 — décoratif, jamais porteur de texte
      radius: "0.5rem",
    },
    dark: {
      background: "#0E0C09", // n-900
      foreground: "#F4F3EE", // n-50
      card: "#1A1813", // n-800
      cardForeground: "#F4F3EE",
      popover: "#1A1813",
      popoverForeground: "#F4F3EE",
      primary: "#C7E6FA", // brand-100 — accent clair sur fonds n-800/n-900
      primaryForeground: "#08527F", // brand-900
      secondary: "#2F2B22", // n-700
      secondaryForeground: "#F4F3EE",
      muted: "#1A1813",
      mutedForeground: "#A8A395",
      accent: "#2F2B22",
      accentForeground: "#C7E6FA",
      destructive: "#F0736C",
      destructiveForeground: "#0E0C09",
      border: "#2F2B22", // n-700
      input: "#2F2B22",
      ring: "#26A2EA", // brand-500
      radius: "0.5rem",
    },
    fonts: {
      // Inter Variable empaquetée localement (public/fonts, alimenté par
      // scripts/prepare.mjs depuis @fontsource-variable/inter).
      // Aucune requête vers fonts.googleapis.com : le nom de police prédéfini
      // « Inter » de Zudoku, lui, chargerait Google Fonts.
      sans: {
        url: "/fonts/inter.css",
        fontFamily:
          '"Inter Variable", "Inter", system-ui, -apple-system, sans-serif',
      },
      // serif et mono doivent être fournis explicitement : laissés vides,
      // Zudoku importe Playfair Display et Geist Mono depuis Google Fonts.
      // La forme « nom de police » n'accepte que sa liste Google : on passe donc
      // par une URL locale, même quand la police est celle du système.
      serif: {
        url: "/fonts/inter.css",
        fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
      },
      mono: {
        url: "/fonts/system.css",
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
      },
    },
  },

  syntaxHighlighting: {
    themes: { light: "github-light", dark: "github-dark-dimmed" },
  },

  search: {
    type: "pagefind",
    maxSubResults: 3,
  },

  docs: {
    files: "/pages/**/*.{md,mdx}",
    defaultOptions: {
      toc: true,
      disablePager: false,
    },
  },

  navigation: [
    {
      type: "category",
      label: "Guides",
      icon: "book-open",
      items: [
        "/accueil",
        "/guides/demarrage-rapide",
        "/guides/authentification",
        "/guides/cles-api",
        "/guides/apporteur-affaires",
        "/guides/credit-wallet",
        "/guides/erreurs",
        "/guides/limites-et-quotas",
        "/guides/perimetre",
        "/guides/webhooks",
      ],
    },
    {
      type: "category",
      label: "Référence",
      icon: "list",
      items: [
        "/reference/codes-erreur",
        "/reference/roles",
        "/reference/glossaire",
      ],
    },
    {
      type: "link",
      to: "/api",
      label: "Référence API",
      icon: "code",
    },
    // Version anglaise : mêmes slugs sous /en/. Masquée de la barre supérieure —
    // on y accède par le sélecteur de langue, pas par un doublon d'onglets.
    {
      type: "category",
      label: "Guides",
      icon: "book-open",
      display: "hide",
      items: [
        "/en/accueil",
        "/en/guides/demarrage-rapide",
        "/en/guides/authentification",
        "/en/guides/cles-api",
        "/en/guides/apporteur-affaires",
        "/en/guides/credit-wallet",
        "/en/guides/erreurs",
        "/en/guides/limites-et-quotas",
        "/en/guides/perimetre",
        "/en/guides/webhooks",
      ],
    },
    {
      type: "category",
      label: "Reference",
      icon: "list",
      display: "hide",
      items: [
        "/en/reference/codes-erreur",
        "/en/reference/roles",
        "/en/reference/glossaire",
      ],
    },
  ],

  redirects: [
    { from: "/", to: "/accueil" },
    { from: "/en", to: "/en/accueil" },
  ],

  apis: apiSource,
};

export default config;
